import { formatRelativeTime } from '@/lib/utils/formatters';
import type { ConversationMessage } from '@/types/conversation';
import { cn } from '@/lib/utils';
import { MarkdownRenderer } from './MarkdownRenderer';
import MessageFilesList from './MessageFilesList';
import CitationsList from './CitationsList';

interface ChatMessageProps {
  message: ConversationMessage;
}

function ChatMessage({ message }: ChatMessageProps) {
  const isUser = message.role === 'user';
  const hasFiles = message.files && message.files.length > 0;
  const hasAnnotations = !isUser && message.annotations && message.annotations.length > 0;

  return (
    <div
      className={cn(
        'flex gap-3 group',
        isUser && 'flex-row-reverse'
      )}
    >
      {/* Message content */}
      <div className={cn('flex-1 space-y-2', isUser && 'flex flex-col items-end')}>
        {/* Files (shown above text for user messages) */}
        {isUser && hasFiles && (
          <MessageFilesList files={message.files!} className="max-w-[80%]" />
        )}

        {/* Text content */}
        <div
          className={cn(
            'text-sm wrap-break-word',
            isUser
              ? 'rounded-lg px-4 py-3 bg-background text-black max-w-[80%] shadow-sm whitespace-pre-wrap'
              : 'max-w-full'
          )}
        >
          {isUser ? message.content : <MarkdownRenderer content={message.content} />}
          {hasAnnotations && <CitationsList annotations={message.annotations!} />}
        </div>

        {/* Timestamp - shown on hover, only for user messages */}
        {isUser && (
          <div className="text-xs text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity">
            {formatRelativeTime(message.createdAt)}
          </div>
        )}
      </div>
    </div>
  );
}

export default ChatMessage;
